import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { questionnaireContent } from '../questionnaire.content';
import { QuestionnaireService } from 'src/app/bar/services/questionnaire.service';
import { Questionnaire } from '@local/client-contracts';
import { Logger } from '@unleash-tech/js-logger';
import { LogService } from '@shared/services';
import { QuestionnaireData, QuestionnaireEnum } from '../questionnaire.component';
import { AppPopupComponent, AppPopupData } from '../../../../components/app-popup/app-popup.component';
import { PopupRef } from '@local/ui-infra';

@UntilDestroy()
@Component({
  selector: 'processing-questionnaire',
  templateUrl: './processing-questionnaire.component.html',
  styleUrls: ['./processing-questionnaire.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessingQuestionnaireComponent implements OnDestroy {
  readonly questionnaireContent = questionnaireContent;
  private processInterval: NodeJS.Timeout;
  private logger: Logger;
  private _data: QuestionnaireData;
  private errorPopupRef: PopupRef<AppPopupComponent, AppPopupData>;
  answered: number;
  completed: number;
  questions: number;
  prepareProcessing: boolean = true;

  @Input() set data(val: QuestionnaireData) {
    this._data = val;
    this.startProcess();
    this.updateStatus(this.data.activeProcess);
  }

  get data(): QuestionnaireData {
    return this._data;
  }

  @Output() processingFinish = new EventEmitter();
  @Output() goBack = new EventEmitter<QuestionnaireEnum>();

  constructor(
    private questionnaireService: QuestionnaireService,
    private cdr: ChangeDetectorRef,
    private logService: LogService
  ) {
    this.logger = this.logService.scope('ProcessingQuestionnaire');
  }

  ngOnDestroy(): void {
    if (this.processInterval) {
      clearInterval(this.processInterval);
      this.processInterval = null;
    }
  }

  async startProcess() {
    try {
      let status = await this.questionnaireService.status(this.data.processId);
      this.updateStatus(status);
      this.processInterval = setInterval(async () => {
        if (this.prepareProcessing || status.pending) {
          try {
            status = await this.questionnaireService.status(this.data.processId);
          } catch (error) {
            this.logger.error('failed to get status rfp', error);
            this.openErrorPopup();
          }
          this.updateStatus(status);
        } else {
          clearInterval(this.processInterval);
          this.processInterval = null;
          if (!this.errorPopupRef) {
            this.processingFinish.emit();
          }
        }
      }, 5000);
    } catch (error) {
      this.logger.error('failed to get status rfp', error);
      this.openErrorPopup();
    }
  }

  private openErrorPopup(errorCode?: Questionnaire.ErrorCodeQuestionnaire) {
    const errorType = errorCode === 'QuestionsNotFound' ? 'emptyColumn' : 'failed';
    this.errorPopupRef = this.questionnaireService.openErrorPopup(errorType);
    const goBackTo = errorCode === 'FailedUpdateXlsxFile' ? QuestionnaireEnum.Upload : QuestionnaireEnum.Mapping;
    this.errorPopupRef.compInstance.primaryButton.subscribe(() => {
      this.goBack.emit(goBackTo);
    });
    this.errorPopupRef.outsidePointerEvents$.pipe().subscribe(async () => {
      this.goBack.emit(goBackTo);
    });
  }

  private updateStatus(status: Questionnaire.QuestionnaireStatusResponse) {
    if (!status) return;
    if (status.status === 'Failed') {
      this.openErrorPopup(status.errorCode);
      return;
    }
    const totalAnswered: number = status.worksheets.reduce((sum, sheet) => sum + sheet.answered, 0);
    this.answered = totalAnswered;
    this.completed = status.completed;
    this.questions = status.pending + status.completed;
    this.prepareProcessing = status.status === 'ExtractingQuestions';
    this.cdr.markForCheck();
  }
}
